import { useEffect, useRef, useState } from 'react';
import TopBar from '../components/TopBar';
import { api } from '../api/client';
import { useAuth } from '../stores/auth';

type Sesion = {
  id: number;
  zona_nombre: string;
  zona_codigo?: string;
  iniciada_en: string;
  patente: string;
  tarifa_hora_centavos: number;
  saldo_reservado_centavos: number;
  estado: string;
};

type Vehiculo = {
  id: number;
  patente: string;
  patente_display: string;
  alias: string | null;
  tipo: string;
  es_favorito: boolean;
};

function inicioMs(iniciada: string): number {
  return new Date(iniciada.replace(' ', 'T')).getTime();
}

function fmtMinutos(mins: number): string {
  const m = Math.floor(mins);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${m % 60}min`;
}

function fmtHora(ts: number): string {
  return new Date(ts).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

function calcTimer(sesion: Sesion) {
  const ahora      = Date.now();
  const inicio     = inicioMs(sesion.iniciada_en);
  const minTransc  = Math.max(0, (ahora - inicio) / 60000);
  const minReserv  = (sesion.saldo_reservado_centavos / sesion.tarifa_hora_centavos) * 60;
  const minRest    = Math.max(0, minReserv - minTransc);
  const costoHasta = Math.min(
    sesion.saldo_reservado_centavos,
    (minTransc / 60) * sesion.tarifa_hora_centavos
  ) / 100;
  const saldoRest  = Math.max(0, sesion.saldo_reservado_centavos / 100 - costoHasta);
  const pct        = Math.min(100, (minTransc / minReserv) * 100);
  const tsVence    = inicio + minReserv * 60000;
  return { minTransc, minRest, costoHasta, saldoRest, pct, tsVence };
}

export default function Estacionar() {
  const ciudadano = useAuth((s) => s.ciudadano);
  const mid = ciudadano?.municipio_id ?? 0;

  const [sesion, setSesion]         = useState<Sesion | null | undefined>(undefined);
  const [vehiculos, setVehiculos]   = useState<Vehiculo[]>([]);
  const [vehiculoId, setVehiculoId] = useState<number | null>(null);
  const [gps, setGps]               = useState<{ lat: number; lng: number } | null>(null);
  const [buscandoGps, setBuscandoGps] = useState(false);
  const [vista, setVista]           = useState<'principal' | 'agregar'>('principal');
  const [nuevaPatente, setNuevaPatente] = useState('');
  const [nuevoTipo, setNuevoTipo]   = useState('auto');
  const [nuevoAlias, setNuevoAlias] = useState('');
  const [loading, setLoading]       = useState(false);
  const [err, setErr]               = useState<string | null>(null);
  const [msg, setMsg]               = useState<string | null>(null);
  const [, setTick] = useState(0);
  const timerVisu  = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerDatos = useRef<ReturnType<typeof setInterval> | null>(null);

  // Tick visual cada 10 s para actualizar el reloj en pantalla
  useEffect(() => {
    timerVisu.current = setInterval(() => setTick((t) => t + 1), 10000);
    return () => { if (timerVisu.current) clearInterval(timerVisu.current); };
  }, []);

  // Refresco de datos cada 60 s
  useEffect(() => {
    timerDatos.current = setInterval(() => cargarDatos(), 60000);
    return () => { if (timerDatos.current) clearInterval(timerDatos.current); };
  }, []);

  useEffect(() => { cargarDatos(); }, []);

  async function cargarDatos() {
    try {
      const [s, v] = await Promise.all([
        api<Sesion | null>(`/estacionamiento/activa?municipio_id=${mid}`),
        api<Vehiculo[]>('/estacionamiento/vehiculos'),
      ]);
      setSesion(s);
      setVehiculos(v);
      if (v.length > 0 && !vehiculoId) setVehiculoId(v[0].id);
    } catch (e: any) {
      setSesion(null);
      setErr('Error cargando datos: ' + e.message);
    }
  }

  async function obtenerGps(): Promise<{ lat: number; lng: number } | null> {
    if (!navigator.geolocation) {
      setErr('GPS no disponible en este dispositivo');
      return null;
    }
    setBuscandoGps(true);
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setGps(coords);
          setBuscandoGps(false);
          resolve(coords);
        },
        () => {
          setBuscandoGps(false);
          setErr('No se pudo obtener el GPS. Asegurate de dar permiso de ubicación.');
          resolve(null);
        },
        { timeout: 10000, maximumAge: 15000 }
      );
    });
  }

  async function iniciar() {
    const veh = vehiculos.find((v) => v.id === vehiculoId);
    if (!veh) return setErr('Seleccioná un vehículo');
    if (mid <= 0) return setErr('Error: sin municipio asignado');
    setErr(null); setMsg(null);
    let coords = gps;
    if (!coords) {
      setMsg('Obteniendo GPS…');
      coords = await obtenerGps();
      if (!coords) return;
    }
    setLoading(true);
    try {
      await api('/estacionamiento/iniciar', {
        method: 'POST',
        body: JSON.stringify({ patente: veh.patente, municipio_id: mid, lat: coords.lat, lng: coords.lng, vehiculo_id: veh.id }),
      });
      setMsg(null);
      await cargarDatos();
    } catch (e: any) {
      setErr(e.message || 'Error al iniciar');
    } finally {
      setLoading(false);
    }
  }

  async function cerrar() {
    if (!sesion) return;
    if (!confirm('¿Cerrar la sesión de estacionamiento?')) return;
    setLoading(true); setErr(null);
    try {
      await api('/estacionamiento/cerrar', {
        method: 'POST',
        body: JSON.stringify({ sesion_id: sesion.id }),
      });
      setSesion(null);
      setMsg('Sesión cerrada. El saldo no utilizado fue liberado.');
    } catch (e: any) {
      setErr(e.message || 'Error al cerrar');
    } finally {
      setLoading(false);
    }
  }

  async function eliminarVehiculo(id: number, patente: string) {
    if (!confirm(`¿Eliminar el vehículo ${patente}?`)) return;
    setErr(null);
    try {
      await api(`/estacionamiento/vehiculos?id=${id}`, { method: 'DELETE' });
      await cargarDatos();
    } catch (e: any) {
      setErr(e.message || 'Error al eliminar');
    }
  }

  async function agregarVehiculo() {
    const p = nuevaPatente.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (p.length < 6) return setErr('Patente inválida (mínimo 6 caracteres)');
    setLoading(true); setErr(null);
    try {
      await api('/estacionamiento/vehiculos', {
        method: 'POST',
        body: JSON.stringify({ patente: p, tipo: nuevoTipo, alias: nuevoAlias || null }),
      });
      setNuevaPatente(''); setNuevoAlias(''); setVista('principal');
      await cargarDatos();
    } catch (e: any) {
      setErr(e.message || 'Error al agregar');
    } finally {
      setLoading(false);
    }
  }

  // ── Vista: agregar vehículo ──────────────────────────────────
  if (vista === 'agregar') {
    return (
      <>
        <TopBar title="Agregar vehículo" back />
        <main className="px-4 py-5 space-y-4 max-w-md mx-auto pb-24">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Patente</label>
            <input
              type="text"
              value={nuevaPatente}
              onChange={(e) => setNuevaPatente(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              placeholder="AB123CD"
              maxLength={10}
              className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-2xl font-mono text-center tracking-wider text-gray-900 focus:border-brand-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Tipo</label>
            <select
              value={nuevoTipo}
              onChange={(e) => setNuevoTipo(e.target.value)}
              className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-900 focus:border-brand-500 outline-none"
            >
              <option value="auto">Auto</option>
              <option value="moto">Moto</option>
              <option value="camioneta">Camioneta</option>
              <option value="utilitario">Utilitario</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Alias (opcional)</label>
            <input
              type="text"
              value={nuevoAlias}
              onChange={(e) => setNuevoAlias(e.target.value)}
              placeholder="Mi auto, El azul…"
              className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-900 focus:border-brand-500 outline-none"
            />
          </div>
          {err && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{err}</div>
          )}
          <button
            onClick={agregarVehiculo}
            disabled={loading}
            className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded-xl py-3.5 font-semibold"
          >
            {loading ? 'Guardando…' : 'Guardar vehículo'}
          </button>
        </main>
      </>
    );
  }

  // ── Vista principal ──────────────────────────────────────────
  const timer = sesion ? calcTimer(sesion) : null;
  const urgente  = timer ? timer.minRest < 15 : false;
  const advertir = timer ? timer.minRest < 30 : false;

  const borderColor = urgente  ? 'border-red-400'
                    : advertir ? 'border-yellow-400'
                    :            'border-green-400';
  const bgColor     = urgente  ? 'bg-red-50'
                    : advertir ? 'bg-yellow-50'
                    :            'bg-green-50';
  const barColor    = urgente  ? '#ef4444'
                    : advertir ? '#f59e0b'
                    :            '#22c55e';
  const textColor   = urgente  ? 'text-red-700'
                    : advertir ? 'text-yellow-700'
                    :            'text-green-700';

  return (
    <>
      <TopBar title="Estacionamiento" back />
      <main className="px-4 py-5 space-y-4 max-w-md mx-auto pb-24">

        {err && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{err}</div>
        )}
        {msg && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded-xl p-3">{msg}</div>
        )}

        {/* ── Sesión activa con timer ── */}
        {sesion && timer && (
          <div className={`${bgColor} border-2 ${borderColor} rounded-2xl p-5 space-y-4`}>

            {/* Patente y zona */}
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide ${textColor}`}>Sesión activa</p>
              <p className="text-2xl font-extrabold font-mono tracking-wider mt-1 text-gray-900">{sesion.patente}</p>
              <p className={`text-sm mt-0.5 ${textColor}`}>{sesion.zona_nombre}</p>
            </div>

            {/* Tiempo transcurrido — grande, tipo reloj */}
            <div className="text-center py-1">
              <p className="text-5xl font-mono font-extrabold text-gray-900 tabular-nums leading-none">
                {fmtMinutos(timer.minTransc)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                desde las {fmtHora(inicioMs(sesion.iniciada_en))}
              </p>
            </div>

            {/* Barra de progreso */}
            <div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="h-3 rounded-full transition-all duration-500"
                  style={{ width: `${timer.pct}%`, backgroundColor: barColor }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>inicio</span>
                <span>vence {fmtHora(timer.tsVence)}</span>
              </div>
            </div>

            {/* Estadísticas */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-white bg-opacity-60 rounded-xl p-2">
                <p className="text-xs text-gray-500 mb-0.5">Tiempo restante</p>
                <p className={`font-bold text-base leading-tight ${urgente ? 'text-red-600' : advertir ? 'text-yellow-600' : 'text-gray-900'}`}>
                  {fmtMinutos(timer.minRest)}
                </p>
              </div>
              <div className="bg-white bg-opacity-60 rounded-xl p-2">
                <p className="text-xs text-gray-500 mb-0.5">Costo hasta ahora</p>
                <p className="font-bold text-base text-gray-900 leading-tight">
                  ${timer.costoHasta.toFixed(2)}
                </p>
              </div>
              <div className="bg-white bg-opacity-60 rounded-xl p-2">
                <p className="text-xs text-gray-500 mb-0.5">Saldo restante</p>
                <p className={`font-bold text-base leading-tight ${urgente ? 'text-red-600' : 'text-gray-900'}`}>
                  ${timer.saldoRest.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Alerta de vencimiento próximo */}
            {urgente && (
              <div className="bg-red-100 border border-red-300 rounded-xl p-3 text-center">
                <p className="text-red-700 font-bold text-sm">⚠ Menos de 15 minutos</p>
                <p className="text-red-600 text-xs mt-0.5">
                  La sesión se cierra a las {fmtHora(timer.tsVence)}. Retirá el vehículo o cargá saldo.
                </p>
              </div>
            )}
            {!urgente && advertir && (
              <div className="bg-yellow-100 border border-yellow-300 rounded-xl p-3 text-center">
                <p className="text-yellow-700 font-semibold text-sm">Quedan menos de 30 minutos</p>
                <p className="text-yellow-600 text-xs mt-0.5">
                  Vence a las {fmtHora(timer.tsVence)}.
                </p>
              </div>
            )}

            {/* Tarifa */}
            <p className="text-xs text-gray-400 text-center">
              Tarifa: ${(sesion.tarifa_hora_centavos / 100).toFixed(2)}/h · Reservado: ${(sesion.saldo_reservado_centavos / 100).toFixed(2)}
            </p>

            <button
              onClick={cerrar}
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl py-3 font-bold"
            >
              {loading ? 'Cerrando…' : 'Cerrar sesión de estacionamiento'}
            </button>
          </div>
        )}

        {/* Sin sesión → iniciar */}
        {sesion === null && (
          <>
            {/* Vehículos */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Tu vehículo</p>
                <button
                  onClick={() => { setVista('agregar'); setErr(null); setMsg(null); }}
                  className="text-xs text-brand-600 hover:text-brand-700 font-semibold"
                >
                  + Agregar
                </button>
              </div>
              {vehiculos.length === 0 ? (
                <div className="bg-white border border-app rounded-xl p-4 text-center shadow-sm">
                  <p className="text-gray-500 text-sm">No tenés vehículos registrados.</p>
                  <button
                    onClick={() => setVista('agregar')}
                    className="mt-2 text-brand-600 text-sm font-semibold"
                  >
                    Agregar ahora →
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {vehiculos.map((v) => (
                    <div
                      key={v.id}
                      className={`rounded-xl border flex items-center gap-3 transition-colors ${
                        vehiculoId === v.id
                          ? 'border-brand-500 bg-brand-50'
                          : 'border-app bg-white'
                      }`}
                    >
                      <button
                        onClick={() => setVehiculoId(v.id)}
                        className="flex-1 text-left px-4 py-3 flex items-center gap-3"
                      >
                        <span className="text-2xl">{v.tipo === 'moto' ? '🛵' : v.tipo === 'camioneta' ? '🚐' : '🚗'}</span>
                        <span>
                          <span className="block font-mono font-bold tracking-wider text-lg text-gray-900">
                            {v.patente_display || v.patente}
                          </span>
                          {v.alias && <span className="text-xs text-gray-500">{v.alias}</span>}
                        </span>
                      </button>
                      <button
                        onClick={() => eliminarVehiculo(v.id, v.patente_display || v.patente)}
                        className="px-3 py-3 text-gray-400 hover:text-red-500 transition-colors text-lg flex-shrink-0"
                        title="Eliminar vehículo"
                      >
                        🗑
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* GPS */}
            <div className="bg-white border border-app rounded-xl p-4 shadow-sm">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Ubicación</p>
              {gps ? (
                <p className="text-xs text-green-600 font-medium">
                  ✓ GPS capturado · {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}
                </p>
              ) : (
                <p className="text-xs text-gray-500">
                  La ubicación se toma al iniciar. El sistema detecta la zona automáticamente.
                </p>
              )}
            </div>

            {vehiculos.length > 0 && (
              <button
                onClick={iniciar}
                disabled={loading || buscandoGps || !vehiculoId}
                className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded-xl py-4 font-bold text-base"
              >
                {buscandoGps ? '📍 Obteniendo GPS…' : loading ? 'Iniciando…' : '🅿️ Iniciar estacionamiento'}
              </button>
            )}

            <p className="text-xs text-gray-400 text-center">
              El sistema detecta automáticamente en qué zona estás por GPS.
              Si no estás en una zona habilitada, el inicio fallará.
            </p>
          </>
        )}

        {sesion === undefined && (
          <p className="text-gray-400 text-sm text-center pt-10">Cargando…</p>
        )}
      </main>
    </>
  );
}
